import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { deleteUser, DerinOrganization, DerinUser } from "../../../api/derin";
import ModalShell from "../CollectionsTab/ModalShell";
import { collectionColors, font, primaryButtonSx, subtitleSx } from "../CollectionsTab/tokens";
import { errorMessageOf } from "../errorMessage";
import { derinColors, derinInputStyles } from "../theme";
import { useToast } from "../Toast";
import AddUserModal from "./AddUserModal";
import { ResendInviteMenuItem, useResendInvite } from "./ResendInvite";
import UserDetailView from "./UserDetailView";
import { dangerNoticeSx, initialsOf, organizationChipSx, organizationLabel } from "./styles";

interface Props {
  users: DerinUser[];
  organizations: DerinOrganization[];
  isLoading: boolean;
  onRefresh: () => void | Promise<void>;
}

const headerCellSx = {
  fontFamily: font,
  fontWeight: 500,
  fontSize: "14px",
  color: collectionColors.textPrimary,
  borderBottom: `1px solid ${collectionColors.border}`,
};

const bodyCellSx = {
  fontFamily: font,
  fontSize: "14px",
  color: collectionColors.textPrimary,
  borderBottom: `1px solid ${collectionColors.border}`,
};

/**
 * Every Derin client user, across organizations. One row per person: the
 * organizations they can open are chips, managed from the user's own page ("Manage user").
 */
const UsersSection: React.FC<Props> = ({ users, organizations, isLoading, onRefresh }) => {
  const { showSuccess, showError } = useToast();
  const [search, setSearch] = useState("");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; user: DerinUser } | null>(null);
  const [managing, setManaging] = useState<DerinUser | null>(null);
  const [deleting, setDeleting] = useState<DerinUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { resend, sendingId } = useResendInvite();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      [u.firstName, u.lastName, u.email, ...u.memberships.map((m) => m.organizationName)]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [users, search]);

  if (managing) {
    return (
      <UserDetailView
        key={managing.id}
        user={users.find((u) => u.id === managing.id) ?? managing}
        organizations={organizations}
        onBack={() => setManaging(null)}
        onChanged={onRefresh}
      />
    );
  }

  const handleDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await deleteUser(deleting.id);
      // Keep the dialog up until the list no longer has them.
      await onRefresh();
      showSuccess(`${deleting.firstName} ${deleting.lastName} was deleted`);
      setDeleting(null);
    } catch (err) {
      showError(errorMessageOf(err));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        background: collectionColors.white,
        border: `1px solid ${collectionColors.border}`,
        borderRadius: "14px",
        mt: 4,
        maxWidth: "1100px",
        mx: "auto",
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontFamily: font, fontWeight: 600, fontSize: "18px", color: collectionColors.textPrimary }}>
            Users
          </Typography>
          <Typography sx={subtitleSx}>Manage user accounts and organization access</Typography>
        </Box>
        <Button
          onClick={() => setIsAddOpen(true)}
          startIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: 18 }} />}
          sx={primaryButtonSx}
        >
          Add User
        </Button>
      </Box>

      {/* Same search box as the Feedback tab. */}
      <Box sx={{ ...derinInputStyles.searchContainer, width: "360px", maxWidth: "100%" }}>
        <SearchIcon sx={{ color: derinColors.textSecondary, fontSize: 16, mr: 1 }} />
        <InputBase
          placeholder="Search by name, email or organization"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={derinInputStyles.searchInput}
        />
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Name</TableCell>
              <TableCell sx={headerCellSx}>Email</TableCell>
              <TableCell sx={headerCellSx}>Organizations</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 48 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ ...bodyCellSx, py: 4, color: collectionColors.textSecondary }}>
                  No users found
                </TableCell>
              </TableRow>
            )}
            {filtered.map((user, index) => (
              <TableRow key={user.id} sx={{ background: index % 2 === 0 ? "rgba(0, 0, 0, 0.03)" : "transparent" }}>
                <TableCell sx={bodyCellSx}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: "#E6F4F1",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "11px",
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {initialsOf(user.firstName)}
                    </Box>
                    {user.firstName} {user.lastName}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx}>{user.email}</TableCell>
                <TableCell sx={bodyCellSx}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                    {user.memberships.length === 0 ? (
                      <Typography sx={{ ...subtitleSx, fontSize: "13px" }}>No organization</Typography>
                    ) : (
                      user.memberships.map((m) => (
                        <Box key={m.organizationId} component="span" sx={organizationChipSx} title={m.role}>
                          {organizationLabel(m.organizationId, m.organizationName, organizations)}
                        </Box>
                      ))
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={bodyCellSx} align="right">
                  <IconButton
                    size="small"
                    aria-label={`Actions for ${user.email}`}
                    onClick={(e) => setMenu({ anchor: e.currentTarget, user })}
                  >
                    <MoreVertIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Menu
        anchorEl={menu?.anchor}
        open={!!menu}
        onClose={() => setMenu(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            setManaging(menu?.user ?? null);
            setMenu(null);
          }}
        >
          Manage user
        </MenuItem>
        <ResendInviteMenuItem
          user={menu?.user}
          sendingId={sendingId}
          onClick={() => {
            if (menu) resend(menu.user);
            setMenu(null);
          }}
        />
        <MenuItem
          sx={{ color: collectionColors.danger }}
          onClick={() => {
            setDeleting(menu?.user ?? null);
            setMenu(null);
          }}
        >
          Delete user
        </MenuItem>
      </Menu>

      {isAddOpen && (
        <AddUserModal
          open
          organizations={organizations}
          onClose={() => setIsAddOpen(false)}
          onSaved={onRefresh}
        />
      )}

      <ModalShell
        open={!!deleting}
        onClose={() => setDeleting(null)}
        title="Delete user permanently?"
        subtitle={
          deleting
            ? `Delete ${deleting.firstName} ${deleting.lastName} (${deleting.email}) and remove all of their organization memberships? The user will no longer be able to access any organization.`
            : undefined
        }
        confirmLabel="Delete user"
        onConfirm={handleDelete}
        isSubmitting={isDeleting}
        danger
      >
        <Box sx={dangerNoticeSx}>This action takes effect immediately and cannot be undone.</Box>
      </ModalShell>
    </Box>
  );
};

export default UsersSection;
